// client/src/components/AdminNotifier.jsx
//
// Escucha en segundo plano (namespace /admin, sala company_<id>) los eventos de
// negocio de la empresa y muestra una notificación del navegador cuando llega una
// cita o una venta NUEVA, con sonido. En la app de Android el aviso lo pinta la
// capa nativa (bridge.js sustituye window.Notification) y, si Android ya cerró la
// app, llega por push de FCM en vez de por este socket. Se monta
// una sola vez para los administradores (roles 1,2,3), así funciona sin importar en
// qué página del panel esté el admin. No pinta nada en pantalla.
import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { requestNotificationPermission, showNotification, playChime, unlockAudio } from '../utils/notify';

const SOCKET_BASE = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : 'http://localhost:5000';

export default function AdminNotifier({ token }) {
  useEffect(() => {
    if (!token) return undefined;
    requestNotificationPermission();
    const onGesture = () => { requestNotificationPermission(); unlockAudio(); };
    window.addEventListener('pointerdown', onGesture, { once: true });

    const socket = io(`${SOCKET_BASE}/admin`, { auth: { token } });
    // requireFocusAway:false → estos eventos importan aunque el admin tenga la pestaña
    // enfocada (puede estar en otra sección del panel).
    // En empresas con calendario por usuario el servidor solo envía este evento al
    // usuario a quien se le agendó la cita, así que suena/notifica solo a él.
    // Al administrador el servidor le manda el texto con el nombre del trabajador
    // al que se le agendó; al trabajador (o a toda la empresa) llega sin textos.
    socket.on('appointment_created', (payload) => {
      playChime();
      showNotification((payload && payload.title) || 'Nueva cita', {
        body: (payload && payload.body) || 'Se agendó una nueva cita.', tag: 'cita-nueva', requireFocusAway: false,
      });
    });
    // Cancelación hecha por el cliente desde la página pública. Los textos vienen
    // del servidor porque incluyen cuál cita era (servicio, fecha y hora).
    socket.on('appointment_cancelled', (payload) => {
      playChime();
      showNotification((payload && payload.title) || 'Cita cancelada', {
        body: (payload && payload.body) || 'Un cliente canceló su cita.',
        tag: 'cita-cancelada',
        requireFocusAway: false,
      });
    });
    // Los textos vienen del servidor porque una venta no siempre es un pedido de
    // la tienda: puede ser un pago aprobado o una factura del cajero.
    socket.on('bill_created', (payload) => {
      playChime();
      showNotification((payload && payload.title) || 'Nuevo pedido', {
        body: (payload && payload.body) || 'Llegó un nuevo pedido.',
        tag: 'pedido-nuevo',
        requireFocusAway: false,
      });
    });

    return () => {
      window.removeEventListener('pointerdown', onGesture);
      socket.disconnect();
    };
  }, [token]);

  return null;
}
